"use client";

import { useEffect } from "react";

import { ChatbotPlaceholder } from "./ChatbotPlaceholder/ChatbotPlaceholder";
import { useChatWidgetRoute } from "./helpers/useChatWidgetRoute";

import { isProduction } from "@/common/constants/env";
import { pushGtmDonorSupportChannel } from "@/common/helpers/gtm";
import { useChatWidgetSuppressed } from "@/common/hooks/useChatWidgetSuppression";
import { RouteType } from "@/common/hooks/amplitude/useLogRouteChangePageVisits";

declare global {
  interface Window {
    Forethought?: (target: string, action: string, ...args: unknown[]) => void;
  }
}

type ForethoughtMessageEvent = MessageEvent<{ event?: string }>;

const hideForethoughtWidget = () => {
  try {
    window.Forethought?.("widget", "hide");
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to hide Forethought widget", error);
  }
};

const showForethoughtWidget = () => {
  try {
    window.Forethought?.("widget", "show");
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("Failed to show Forethought widget", error);
  }
};

export const ChatWidgetVisibilityManager = () => {
  const { isClientReady, pathWithoutLocale, routeType, isHiddenByRoute } = useChatWidgetRoute();
  const suppressed = useChatWidgetSuppressed();
  const shouldHideWidgets = isHiddenByRoute || suppressed;

  useEffect(() => {
    if (isClientReady && routeType === RouteType.PublicRoute) {
      pushGtmDonorSupportChannel({ donor_support_channel: "forethought" });
    }
  }, [isClientReady, pathWithoutLocale, routeType]);

  useEffect(() => {
    if (!isClientReady) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    if (!shouldHideWidgets) {
      showForethoughtWidget();
      return;
    }

    const cleanupFns: Array<() => void> = [];

    if (window.Forethought) {
      hideForethoughtWidget();
    } else {
      const forethoughtListener = (event: ForethoughtMessageEvent) => {
        if (event.data?.event === "forethoughtWidgetLoaded") {
          hideForethoughtWidget();
        }
      };

      window.addEventListener("message", forethoughtListener);
      cleanupFns.push(() => window.removeEventListener("message", forethoughtListener));
    }

    return () => {
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [isClientReady, shouldHideWidgets]);

  if (isProduction || !isClientReady) {
    return null;
  }

  return shouldHideWidgets ? null : <ChatbotPlaceholder />;
};
